/**
 * DiscoveryCardPanel.jsx — iter849 (Feb 2026)
 *
 * Educational Discovery Card panel — displays AI-generated learning
 * content for the 8 rare/lesser-known BDI subtypes (medullary, tubular,
 * papillary, micropapillary, neuroendocrine, complex sclerosing lesion,
 * granulomatous mastitis, duct ectasia, intraductal papilloma).
 *
 * Fetched lazily on first expand. Content is DISEASE-specific (not
 * patient-personalised) so cache is shared across all users — one Claude
 * call per (disease_id, lang, prompt_version) forever.
 *
 * Strict UX rules:
 *   · Educational tone; NEVER diagnostic/prognostic.
 *   · Disclaimer + limitations shown at the bottom of every card.
 *   · Empty/error/loading states handled defensively.
 */
import React, { useState, useCallback } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  GraduationCap, Loader2, RefreshCw, Users, Stethoscope,
  Salad, MessageCircle, AlertTriangle, ChevronDown, ChevronUp,
} from "lucide-react";
import { useAuth } from "@/App";

const ELIGIBLE_IDS = new Set([
  "tubular_carcinoma", "medullary_carcinoma", "invasive_papillary_carcinoma",
  "neuroendocrine_carcinoma", "micropapillary_carcinoma",
  "complex_sclerosing_lesion", "duct_ectasia", "granulomatous_mastitis",
  "intraductal_papilloma",
]);

const TX = {
  bg: {
    button: "Discovery Card",
    subtitle: "Образователна карта (AI · Claude Haiku 4.5)",
    loading: "Генерира се образователна карта…",
    error: "Неуспешно зареждане на Discovery Card.",
    retry: "Опитай пак",
    refresh: "Обнови",
    cached: "от кеш",
    section_overview: "За заболяването",
    section_family: "Значение за фамилна анамнеза",
    section_screening: "Кога да поискате допълнително изследване",
    section_nutrients: "Nutrient bundles",
    section_ask_doctor: "Въпроси към Вашия лекар",
    empty_field: "Няма данни за тази секция.",
  },
  en: {
    button: "Discovery Card",
    subtitle: "Educational card (AI · Claude Haiku 4.5)",
    loading: "Generating educational card…",
    error: "Failed to load Discovery Card.",
    retry: "Retry",
    refresh: "Refresh",
    cached: "cached",
    section_overview: "About this condition",
    section_family: "Family history relevance",
    section_screening: "When to request additional screening",
    section_nutrients: "Nutrient bundles",
    section_ask_doctor: "Questions to ask your physician",
    empty_field: "No data available for this section.",
  },
  es: {
    button: "Discovery Card",
    subtitle: "Tarjeta educativa (IA · Claude Haiku 4.5)",
    loading: "Generando tarjeta educativa…",
    error: "Error al cargar la Discovery Card.",
    retry: "Reintentar",
    refresh: "Actualizar",
    cached: "en caché",
    section_overview: "Sobre esta condición",
    section_family: "Relevancia de la historia familiar",
    section_screening: "Cuándo solicitar cribado adicional",
    section_nutrients: "Nutrient bundles",
    section_ask_doctor: "Preguntas para su médico",
    empty_field: "Sin datos disponibles.",
  },
};

export function isDiscoveryEligible(diseaseId) {
  return ELIGIBLE_IDS.has(diseaseId);
}

export default function DiscoveryCardPanel({ diseaseId, diseaseName, lang = "bg", isDark = false }) {
  const { API } = useAuth();
  const t = TX[lang] || TX.bg;

  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const fetchCard = useCallback(async (force = false) => {
    setLoading(true);
    setErr(null);
    try {
      const url = `${API}/bhi/disease-intelligence/discovery-card/${diseaseId}?lang=${lang}${force ? "&force_refresh=true" : ""}`;
      const { data: resp } = await axios.get(url);
      setData(resp);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || String(e));
    } finally {
      setLoading(false);
    }
  }, [API, diseaseId, lang]);

  const handleToggle = () => {
    const next = !open;
    setOpen(next);
    if (next && !data && !loading) fetchCard(false);
  };

  if (!isDiscoveryEligible(diseaseId)) return null;

  const card = data?.card || null;

  return (
    <div
      className={`rounded-xl border-2 ${isDark ? "border-indigo-500/30 bg-indigo-500/5" : "border-indigo-200 bg-indigo-50/40"}`}
      data-testid={`discovery-card-panel-${diseaseId}`}
    >
      <button
        type="button"
        onClick={handleToggle}
        className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-left hover:bg-black/5 dark:hover:bg-white/5 rounded-xl transition-colors`}
        data-testid={`discovery-card-toggle-${diseaseId}`}
      >
        <div className="flex items-center gap-2 min-w-0">
          <GraduationCap className={`w-4 h-4 shrink-0 ${isDark ? "text-indigo-300" : "text-indigo-600"}`} />
          <div className="min-w-0">
            <div className={`text-xs font-bold ${isDark ? "text-indigo-200" : "text-indigo-800"}`}>
              {t.button}
            </div>
            <div className="text-[10px] opacity-60 truncate">{t.subtitle}</div>
          </div>
        </div>
        {open ? <ChevronUp className="w-4 h-4 opacity-60" /> : <ChevronDown className="w-4 h-4 opacity-60" />}
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.22 }}
            className="overflow-hidden"
          >
            <div className="px-3 pb-3 space-y-3 text-xs">
              {loading && (
                <div className="flex items-center gap-2 py-6 text-sm opacity-70" data-testid={`discovery-card-loading-${diseaseId}`}>
                  <Loader2 className="w-4 h-4 animate-spin" /> {t.loading}
                </div>
              )}

              {err && !loading && (
                <div className="py-4" data-testid={`discovery-card-error-${diseaseId}`}>
                  <div className="flex items-center gap-2 text-red-500 mb-2">
                    <AlertTriangle className="w-4 h-4" /> {t.error}
                  </div>
                  <div className="text-[10px] opacity-60 mb-2">{String(err).slice(0, 240)}</div>
                  <button
                    type="button"
                    onClick={() => fetchCard(false)}
                    className="px-2.5 py-1 rounded-lg text-[11px] font-semibold bg-slate-200 dark:bg-slate-700"
                  >
                    {t.retry}
                  </button>
                </div>
              )}

              {card && !loading && !err && (
                <>
                  {data?.cached && (
                    <div className="text-[9px] uppercase tracking-wider opacity-50 -mt-1">
                      {t.cached} · {(data?.generated_at || "").slice(0, 10)}
                    </div>
                  )}

                  {card.overview && (
                    <Section icon={GraduationCap} label={t.section_overview} isDark={isDark} testid={`discovery-overview-${diseaseId}`}>
                      <p className="leading-relaxed">{card.overview}</p>
                    </Section>
                  )}

                  {card.family_history_relevance && (
                    <Section icon={Users} label={t.section_family} isDark={isDark} testid={`discovery-family-${diseaseId}`}>
                      <p className="leading-relaxed">{card.family_history_relevance}</p>
                    </Section>
                  )}

                  {Array.isArray(card.screening_triggers) && card.screening_triggers.length > 0 && (
                    <Section icon={Stethoscope} label={t.section_screening} isDark={isDark} testid={`discovery-screening-${diseaseId}`}>
                      <ul className="space-y-1">
                        {card.screening_triggers.map((s, i) => (
                          <li key={i} className="flex items-start gap-1.5">
                            <span className={isDark ? "text-indigo-300" : "text-indigo-600"}>▸</span>
                            <span>{s}</span>
                          </li>
                        ))}
                      </ul>
                    </Section>
                  )}

                  {Array.isArray(card.nutrient_bundles) && card.nutrient_bundles.length > 0 && (
                    <Section icon={Salad} label={t.section_nutrients} isDark={isDark} testid={`discovery-nutrients-${diseaseId}`}>
                      <div className="grid gap-2">
                        {card.nutrient_bundles.map((b, i) => (
                          <div
                            key={i}
                            className={`rounded-lg p-2 border ${isDark ? "border-emerald-500/30 bg-emerald-500/5" : "border-emerald-200 bg-emerald-50/70"}`}
                            data-testid={`discovery-nutrient-bundle-${diseaseId}-${i}`}
                          >
                            <div className={`font-bold text-[11px] ${isDark ? "text-emerald-300" : "text-emerald-800"}`}>
                              {b.name || b.title || `Bundle ${i + 1}`}
                            </div>
                            {b.why && (
                              <div className="text-[10px] opacity-70 mt-0.5 italic">{b.why}</div>
                            )}
                            {Array.isArray(b.foods) && b.foods.length > 0 && (
                              <div className="mt-1 flex flex-wrap gap-1">
                                {b.foods.map((f, j) => (
                                  <span
                                    key={j}
                                    className={`px-1.5 py-0.5 rounded-full text-[9px] ${isDark ? "bg-emerald-500/20 text-emerald-300" : "bg-emerald-100 text-emerald-800"}`}
                                  >
                                    {f}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </Section>
                  )}

                  {Array.isArray(card.clinician_talking_points) && card.clinician_talking_points.length > 0 && (
                    <Section icon={MessageCircle} label={t.section_ask_doctor} isDark={isDark} testid={`discovery-ask-doctor-${diseaseId}`}>
                      <ul className="space-y-1.5">
                        {card.clinician_talking_points.map((q, i) => (
                          <li
                            key={i}
                            className={`rounded-lg p-2 border-l-4 ${isDark ? "bg-amber-500/10 border-amber-500/60 text-amber-100" : "bg-amber-50 border-amber-400 text-amber-900"}`}
                          >
                            {q}
                          </li>
                        ))}
                      </ul>
                    </Section>
                  )}

                  {data?.disclaimer && (
                    <div className={`text-[10px] italic opacity-70 pt-2 border-t ${isDark ? "border-white/10" : "border-black/5"}`}>
                      {data.disclaimer}
                    </div>
                  )}

                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={() => fetchCard(true)}
                      disabled={loading}
                      className="mt-1 px-2 py-1 rounded-lg text-[10px] font-semibold bg-slate-100 text-slate-700 dark:bg-slate-500/20 dark:text-slate-300 hover:opacity-90 flex items-center gap-1"
                      data-testid={`discovery-card-refresh-${diseaseId}`}
                    >
                      <RefreshCw className="w-3 h-3" /> {t.refresh}
                    </button>
                  </div>
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function Section({ icon: Icon, label, isDark, children, testid }) {
  return (
    <div data-testid={testid}>
      <div className={`flex items-center gap-1.5 text-[10px] uppercase font-bold mb-1 ${isDark ? "text-indigo-200" : "text-indigo-800"}`}>
        <Icon className="w-3 h-3" /> {label}
      </div>
      <div className={`${isDark ? "text-gray-200" : "text-gray-800"}`}>{children}</div>
    </div>
  );
}
